/**
 * LieuSelect — sélecteur de lieu du référentiel (S4-b).
 *
 * Sert au partage de bâtiment : rattacher une école, une crèche ou un matériel
 * à un lieu existant. Cas fréquent : une maternelle et une élémentaire dans les
 * mêmes murs pointent vers le même lieu.
 *
 * Charge les lieux via l'API admin (permission admin.lieu.read, que le manager
 * possède). Autonome : il se recharge seul.
 */
import { useEffect, useState } from 'react';
import { api, type LieuDTO } from '@/lib/api';

const SELECT_CLS =
  'h-10 w-full rounded-lg border border-border bg-surface/60 backdrop-blur-glass px-3 text-sm text-ink shadow-soft transition-all focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg hover:border-border-strong';

interface LieuSelectProps {
  value: string | null | undefined;
  onChange: (lieuId: string | null) => void;
  /** Filtre optionnel sur le code de type (ex. 'ecole', 'creche'), pour ne
   *  proposer que les lieux pertinents. Non fourni = tous les lieux actifs. */
  disabled?: boolean;
  className?: string;
}

export function LieuSelect({ value, onChange, disabled, className }: LieuSelectProps) {
  const [lieux, setLieux] = useState<LieuDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await api.admin.lieux.list();
        if (!annule) setLieux(r.lieux.filter((l) => l.actif));
      } catch {
        if (!annule) setLieux([]);
      } finally {
        if (!annule) setLoading(false);
      }
    })();
    return () => { annule = true; };
  }, []);

  return (
    <select
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
      className={className ?? SELECT_CLS}
      disabled={disabled || loading}
    >
      <option value="">{loading ? 'Chargement…' : '— Aucun lieu rattaché —'}</option>
      {lieux.map((l) => (
        <option key={l.id} value={l.id}>
          {l.nom}{l.typeLibelle ? ` (${l.typeLibelle})` : ''}
        </option>
      ))}
    </select>
  );
}
